import React, { useMemo } from 'react';
import { AlertTriangle, Hourglass, Info, Ship } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCompany } from '@/contexts/CompanyContext';
import { fmtDate } from '@/features/dashboard/data/claudeMockData';
import { containerStatusBadge, containerStatusLabel } from '@/features/containers/constants/statuses';
import type { CostEstimate, EstimateBooking, Money } from '../interfaces/costs.interface';
import { categoryLabel, fmtMoney, fmtPrice, fmtTotals, sumByCurrency } from '../utils/costs';

type Lang = 'ro' | 'en';

interface EstimatePanelProps {
  estimate?: CostEstimate;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onShowTariffs: () => void;
}

const MONTHS_RO = ['ian.', 'feb.', 'mar.', 'apr.', 'mai', 'iun.', 'iul.', 'aug.', 'sept.', 'oct.', 'nov.', 'dec.'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const monthLabel = (key: string, lang: Lang) => {
  const [y, m] = key.split('-').map(Number);
  return `${(lang === 'ro' ? MONTHS_RO : MONTHS_EN)[m - 1]} ${y}`;
};

type WithVat = { plusVat?: boolean } | null;

/** true doar dacă TVA-ul se adaugă la TOATE sumele (firmele unui cont pot avea regimuri diferite). */
const vatOnAll = (list: WithVat[]) => {
  const priced = list.filter((m): m is { plusVat?: boolean } => !!m);
  return priced.length > 0 && priced.every((m) => m.plusVat);
};
/** true dacă niciuna dintre sume nu are TVA. */
const vatOnNone = (list: WithVat[]) => {
  const priced = list.filter((m): m is { plusVat?: boolean } => !!m);
  return priced.length > 0 && priced.every((m) => !m.plusVat);
};

const containersOf = (list: EstimateBooking[]) => list.reduce((n, b) => n + b.containers.reduce((s, c) => s + c.count, 0), 0);

function VatTag({ show, lang }: { show?: boolean; lang: Lang }) {
  return show ? <span className="cst-vat">{lang === 'ro' ? '+ TVA' : '+ VAT'}</span> : null;
}

/** Suma netă + „+ TVA” când TVA-ul se adaugă la facturare pentru acest client. */
function PriceWithVat({ money, lang }: { money: (Money & { plusVat?: boolean }) | null; lang: Lang }) {
  if (!money) return <span className="mono">—</span>;
  return (
    <span className="cst-price">
      <span className="mono">{fmtPrice(money, lang)}</span>
      <VatTag show={money.plusVat} lang={lang} />
    </span>
  );
}

/** Navlul unui transport; fără ofertă atribuită → marcaj explicit, nu o linie goală. */
function FreightValue({ booking, lang }: { booking: EstimateBooking; lang: Lang }) {
  const isRo = lang === 'ro';
  if (booking.freight) return <PriceWithVat money={booking.freight} lang={lang} />;
  return (
    <span
      className="cst-unassigned"
      title={
        isRo
          ? 'Oferta de navlu nu a fost încă atribuită acestui transport. Prețul apare aici imediat după atribuire.'
          : 'No freight offer has been assigned to this shipment yet. The price appears here as soon as it is assigned.'
      }
    >
      <Hourglass size={12} strokeWidth={2.2} />
      {isRo ? 'Navlu neatribuit' : 'Freight not assigned'}
    </span>
  );
}

/** Totalul pe monede: prima monedă mare, restul dedesubt. */
function BigTotal({ list, lang, vat }: { list: Money[]; lang: Lang; vat?: boolean }) {
  if (list.length === 0) return <div className="cst-sum-value">—</div>;
  const [first, ...rest] = list;
  return (
    <>
      <div className="cst-sum-value">
        <span className="n">{fmtMoney(first.amount, lang)}</span>
        <span className="u">{first.currency}</span>
        <VatTag show={vat} lang={lang} />
      </div>
      {rest.length > 0 && (
        <div className="cst-sum-extra">
          + {fmtTotals(rest, lang)} <VatTag show={vat} lang={lang} />
        </div>
      )}
    </>
  );
}

function Containers({ booking, lang }: { booking: EstimateBooking; lang: Lang }) {
  return (
    <span className="cst-ctns">
      {booking.containers.map((c) => (
        <span
          key={c.label + c.category}
          className={'cst-ctn' + (c.category !== 'standard' ? ' ' + c.category : '')}
          title={categoryLabel(c.category, lang)}
        >
          {c.count}×{c.label}
          {c.category === 'imo' && ' IMO'}
        </span>
      ))}
    </span>
  );
}

const EstimatePanel: React.FC<EstimatePanelProps> = ({ estimate, isLoading, error, onRetry, onShowTariffs }) => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const { companies, isAllSelected } = useCompany();
  const companyOf = (id: number | null) => (id == null ? undefined : companies.find((c) => c.id === id));

  const bookings = estimate?.bookings ?? [];
  const months = estimate?.months ?? [];

  const freightTotals = useMemo(() => sumByCurrency(bookings.map((b) => b.freight)), [bookings]);
  const localeTotals = useMemo(() => sumByCurrency(bookings.map((b) => b.locale)), [bookings]);
  const containerCount = containersOf(bookings);
  const withoutFreight = bookings.filter((b) => !b.freight);
  const ctnWithoutPrice = containersOf(withoutFreight);
  // „+ TVA” pe un total doar dacă se aplică la toate sumele lui.
  const freightTotalVat = vatOnAll(bookings.map((b) => b.freight));
  const localeTotalVat = vatOnAll(bookings.map((b) => b.locale));

  /** „5 containere fără preț încă” — containerele transporturilor fără navlu atribuit. */
  const noPriceLabel = (n: number) =>
    isRo ? `${n} ${n === 1 ? 'container fără preț' : 'containere fără preț'} încă` : `${n} container${n === 1 ? '' : 's'} not priced yet`;

  const byMonth = useMemo(
    () =>
      months.map((key) => {
        const list = bookings.filter((b) => (b.eta || '').slice(0, 7) === key);
        return {
          key,
          count: list.length,
          freight: sumByCurrency(list.map((b) => b.freight)),
          noPrice: containersOf(list.filter((b) => !b.freight)),
          locale: sumByCurrency(list.map((b) => b.locale)),
          freightVat: vatOnAll(list.map((b) => b.freight)),
          localeVat: vatOnAll(list.map((b) => b.locale)),
        };
      }),
    [months, bookings],
  );
  const outsideWindow = bookings.filter((b) => !b.eta || !months.includes(b.eta.slice(0, 7))).length;

  if (error) {
    return (
      <div className="card" style={{ padding: 0 }}>
        <div className="empty">
          <div className="icon">
            <AlertTriangle size={22} />
          </div>
          <div className="fw-600">{isRo ? 'Estimarea nu a putut fi încărcată' : 'The estimate could not be loaded'}</div>
          <button type="button" className="btn btn-sm mt-2" onClick={onRetry}>
            {isRo ? 'Reîncearcă' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const loading = isLoading && !estimate;

  return (
    <>
      {/* Sumar: navlu + locale */}
      <div className="card cst-summary">
        <div className="cst-sum">
          <div className="cst-sum-top">
            <span className="cst-sum-label">
              <span className="lbl-dot" style={{ background: 'var(--st-info)' }} />
              {isRo ? 'Navlu ofertat' : 'Quoted ocean freight'}
            </span>
            {!loading && ctnWithoutPrice > 0 && (
              <span className="cst-sum-pill" title={isRo ? 'Nu sunt incluse în total' : 'Not included in the total'}>
                {ctnWithoutPrice} {isRo ? 'fără preț' : 'not priced'}
              </span>
            )}
          </div>
          {loading ? <div className="cst-sum-value">—</div> : <BigTotal list={freightTotals} lang={lang} vat={freightTotalVat} />}
          <div className="cst-sum-sub">
            {ctnWithoutPrice > 0
              ? isRo
                ? `Pentru ${containerCount - ctnWithoutPrice} din ${containerCount} containere pe drum`
                : `For ${containerCount - ctnWithoutPrice} of ${containerCount} containers on the way`
              : isRo
                ? `${bookings.length} ${bookings.length === 1 ? 'transport' : 'transporturi'} pe drum · ${containerCount} containere`
                : `${bookings.length} shipment${bookings.length === 1 ? '' : 's'} on the way · ${containerCount} containers`}
          </div>
        </div>
        <div className="cst-sum">
          <div className="cst-sum-top">
            <span className="cst-sum-label">
              <span className="lbl-dot" style={{ background: 'var(--accent)' }} />
              {isRo ? 'Cheltuieli locale estimate' : 'Estimated local charges'}
            </span>
          </div>
          {loading ? <div className="cst-sum-value">—</div> : <BigTotal list={localeTotals} lang={lang} vat={localeTotalVat} />}
          <div className="cst-sum-sub">
            {isRo ? 'THC la sosirea în Constanța' : 'THC on arrival in Constanța'}
            {vatOnNone(bookings.map((b) => b.locale)) && (isRo ? ', fără TVA' : ', no VAT')}
          </div>
        </div>
      </div>

      {/* Pe luni, după ETA */}
      <div className="card mb-4">
        <div className="card-header">
          <div>
            <div className="title">{isRo ? 'Pe luni, după data sosirii' : 'By month, by arrival date'}</div>
            <div className="sub hidden-mobile">
              {isRo ? 'Navlu ofertat și cheltuieli locale pentru transporturile care sosesc' : 'Quoted freight and local charges for arriving shipments'}
            </div>
          </div>
        </div>
        <div className="cst-months">
          {byMonth.map((m, i) => (
            <div key={m.key} className={'cst-month' + (i === 0 ? ' current' : '') + (m.count === 0 ? ' is-empty' : '')}>
              <div className="cst-month-name">{monthLabel(m.key, lang)}</div>
              <div className="cst-month-count">
                {m.count} {isRo ? (m.count === 1 ? 'transport' : 'transporturi') : m.count === 1 ? 'shipment' : 'shipments'}
              </div>
              <div className="cst-month-row">
                <span className="k">{isRo ? 'Navlu' : 'Freight'}</span>
                <span className="v">
                  <span className="mono">{m.freight.length || m.noPrice === 0 ? fmtTotals(m.freight, lang) : ''}</span>
                  {m.freight.length > 0 && <VatTag show={m.freightVat} lang={lang} />}
                </span>
                {m.noPrice > 0 && (
                  <span className="cst-month-unassigned">
                    {m.freight.length ? '+ ' : ''}
                    {noPriceLabel(m.noPrice)}
                  </span>
                )}
              </div>
              <div className="cst-month-row">
                <span className="k">{isRo ? 'Locale' : 'Local'}</span>
                <span className="v">
                  <span className="mono">{fmtTotals(m.locale, lang)}</span>
                  {m.locale.length > 0 && <VatTag show={m.localeVat} lang={lang} />}
                </span>
              </div>
            </div>
          ))}
        </div>
        {outsideWindow > 0 && (
          <div className="cst-foot muted fs-12">
            {isRo
              ? `${outsideWindow} ${outsideWindow === 1 ? 'transport nu are' : 'transporturi nu au'} încă ETA în următoarele 6 luni — apar în lista de mai jos.`
              : `${outsideWindow} shipment${outsideWindow === 1 ? ' has' : 's have'} no ETA within the next 6 months yet — listed below.`}
          </div>
        )}
      </div>

      {/* Transporturi pe drum */}
      <div className="card mb-4">
        <div className="card-header">
          <div>
            <div className="title cst-title">
              <Ship size={15} />
              {isRo ? 'Transporturi pe drum' : 'Shipments on the way'}
            </div>
            <div className="sub hidden-mobile">
              {isRo ? 'Costul estimat pentru fiecare booking, la sosirea în Constanța' : 'Estimated cost per booking on arrival in Constanța'}
            </div>
          </div>
          <span className="cst-count-pill">{bookings.length}</span>
        </div>
        {bookings.length === 0 ? (
          <div className="empty">
            <div className="fw-600">{loading ? (isRo ? 'Se încarcă…' : 'Loading…') : isRo ? 'Niciun transport pe drum' : 'No shipments on the way'}</div>
          </div>
        ) : (
          <>
            <div className="table-wrap hidden-mobile">
              <table className="data compact">
                <thead>
                  <tr>
                    <th>Booking / B/L</th>
                    <th>Status</th>
                    <th>ETA</th>
                    <th>{isRo ? 'Containere' : 'Containers'}</th>
                    <th className="text-right">{isRo ? 'Navlu ofertat' : 'Quoted freight'}</th>
                    <th className="text-right">{isRo ? 'Cheltuieli locale' : 'Local charges'}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const co = isAllSelected ? companyOf(b.companyId) : undefined;
                    return (
                      <tr key={b.bookingId}>
                        <td>
                          <div className="mono fw-600 fs-13">{b.blCodes[0] || b.bookingNumber || '—'}</div>
                          <div className="muted fs-11 mt-1">
                            {b.blCodes.length > 1 && <span className="mono">+{b.blCodes.length - 1} B/L · </span>}
                            {b.bookingNumber && b.bookingNumber !== b.blCodes[0] && <span className="mono">{b.bookingNumber} · </span>}
                            {co && (
                              <span className="cst-co">
                                <span className="swatch" style={{ background: co.color }} />
                                {co.short}
                              </span>
                            )}
                            {b.vesselName}
                          </div>
                        </td>
                        <td>
                          {b.status && (
                            <span className={'badge ' + containerStatusBadge(b.status)}>
                              <span className="dot" />
                              {containerStatusLabel(b.status, lang)}
                            </span>
                          )}
                        </td>
                        <td className="nowrap">{b.eta ? fmtDate(b.eta, lang) : '—'}</td>
                        <td>
                          <Containers booking={b} lang={lang} />
                        </td>
                        <td className="text-right nowrap">
                          <FreightValue booking={b} lang={lang} />
                        </td>
                        <td className="text-right nowrap">
                          <PriceWithVat money={b.locale} lang={lang} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="fw-600">
                      Total
                    </td>
                    <td className="text-right fw-600 nowrap">
                      <span className="cst-price">
                        <span className="mono">{fmtTotals(freightTotals, lang)}</span>
                        <VatTag show={freightTotalVat} lang={lang} />
                      </span>
                      {ctnWithoutPrice > 0 && <div className="cst-total-note">+ {noPriceLabel(ctnWithoutPrice)}</div>}
                    </td>
                    <td className="text-right fw-600 nowrap">
                      <span className="cst-price">
                        <span className="mono">{fmtTotals(localeTotals, lang)}</span>
                        <VatTag show={localeTotalVat} lang={lang} />
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Telefon: un rând compact pe transport */}
            <div className="show-mobile">
              {bookings.map((b) => {
                const co = isAllSelected ? companyOf(b.companyId) : undefined;
                return (
                  <div key={b.bookingId} className="cst-mrow">
                    <div className="cst-mrow-top">
                      <span className="mono fw-600 cst-mrow-code">{b.blCodes[0] || b.bookingNumber || '—'}</span>
                      {b.status && (
                        <span className={'badge ' + containerStatusBadge(b.status)}>
                          <span className="dot" />
                          {containerStatusLabel(b.status, lang)}
                        </span>
                      )}
                    </div>
                    <div className="cst-mrow-meta">
                      {co && (
                        <span className="cst-co">
                          <span className="swatch" style={{ background: co.color }} />
                          {co.short}
                        </span>
                      )}
                      <span>ETA {b.eta ? fmtDate(b.eta, lang) : '—'}</span>
                      <Containers booking={b} lang={lang} />
                    </div>
                    <div className="cst-mrow-money">
                      <div>
                        <span className="k">{isRo ? 'Navlu' : 'Freight'}</span>
                        <FreightValue booking={b} lang={lang} />
                      </div>
                      <div>
                        <span className="k">{isRo ? 'Locale' : 'Local'}</span>
                        <PriceWithVat money={b.locale} lang={lang} />
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="cst-mtotal">
                <div>
                  <span className="k">{isRo ? 'Total navlu' : 'Total freight'}</span>
                  <span className="cst-price">
                    <span className="mono">{fmtTotals(freightTotals, lang)}</span>
                    <VatTag show={freightTotalVat} lang={lang} />
                  </span>
                </div>
                <div>
                  <span className="k">{isRo ? 'Total locale' : 'Total local'}</span>
                  <span className="cst-price">
                    <span className="mono">{fmtTotals(localeTotals, lang)}</span>
                    <VatTag show={localeTotalVat} lang={lang} />
                  </span>
                </div>
              </div>
            </div>

            {withoutFreight.length > 0 && (
              <div className="cst-foot cst-foot-warn fs-12">
                <Hourglass size={13} />
                <span>
                  {isRo
                    ? `„Navlu neatribuit”: ${noPriceLabel(ctnWithoutPrice)} (${withoutFreight.length === 1 ? 'un transport' : `${withoutFreight.length} transporturi`} fără ofertă de navlu atribuită). Prețul apare imediat după atribuire; până atunci nu e inclus în totaluri.`
                    : `“Freight not assigned”: ${noPriceLabel(ctnWithoutPrice)} (${withoutFreight.length === 1 ? 'one shipment' : `${withoutFreight.length} shipments`} without an assigned freight offer). The price appears as soon as it is assigned; until then it is not included in the totals.`}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="cst-note">
        <Info size={15} />
        <div>
          {isRo ? (
            <>
              Sumele sunt <b>estimative</b> și sunt afișate fără TVA; „+ TVA” arată unde se adaugă TVA la facturare. Navlul este cel
              din oferta atribuită fiecărui booking; cheltuielile locale sunt cele din oferta dumneavoastră sau, în lipsa lor, tariful
              standard valabil la data sosirii și se facturează în lei, la cursul din ziua facturii.{' '}
              <button type="button" className="cst-link" onClick={onShowTariffs}>
                Vedeți tarifele
              </button>
            </>
          ) : (
            <>
              Amounts are <b>estimates</b> and shown excluding VAT; “+ VAT” marks where VAT is added on the invoice. Freight comes from
              the offer assigned to each booking; local charges come from your offer or, failing that, the standard tariff valid on
              arrival, and are invoiced in RON at the exchange rate on the invoice date.{' '}
              <button type="button" className="cst-link" onClick={onShowTariffs}>
                See the tariffs
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EstimatePanel;
